import { useState, useEffect } from 'react';

export interface Currency {
  code: string;
  symbol: string;
  name: string;
  rate: number; // Rate against USD base
}

const CURRENCIES: Currency[] = [
  { code: 'INR', symbol: '₹', name: 'Indian Rupee', rate: 83.12 },
  { code: 'USD', symbol: '$', name: 'US Dollar', rate: 1.0 },
  { code: 'EUR', symbol: '€', name: 'Euro', rate: 0.85 },
  { code: 'GBP', symbol: '£', name: 'British Pound', rate: 0.73 },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen', rate: 149.5 },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', rate: 1.35 },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', rate: 1.52 },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc', rate: 0.88 },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', rate: 7.24 },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar', rate: 1.34 }
];

const COUNTRY_TO_CURRENCY: Record<string, string> = {
  'IN': 'INR',
  'US': 'USD',
  'GB': 'GBP',
  'CA': 'CAD',
  'AU': 'AUD',
  'JP': 'JPY',
  'CH': 'CHF',
  'CN': 'CNY',
  'SG': 'SGD',
  'DE': 'EUR',
  'FR': 'EUR',
  'IT': 'EUR',
  'ES': 'EUR',
  'NL': 'EUR'
};

export const useCurrency = () => {
  const [currentCurrency, setCurrentCurrency] = useState<Currency>(
    CURRENCIES.find(c => c.code === 'INR') || CURRENCIES[0]
  );
  
  const [detectedCountry, setDetectedCountry] = useState<string>('IN');

  useEffect(() => {
    // Try to detect user's location
    const detectLocation = async () => {
      try {
        // First try stored preference
        const stored = localStorage.getItem('preferred-currency');
        if (stored) {
          const currency = CURRENCIES.find(c => c.code === stored);
          if (currency) {
            setCurrentCurrency(currency);
            return;
          }
        }

        // Then try to detect via timezone
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        let countryCode = 'IN'; // Default to India
        
        if (timezone.includes('America')) {
          countryCode = timezone.includes('Toronto') || timezone.includes('Vancouver') ? 'CA' : 'US';
        } else if (timezone.includes('Europe')) {
          countryCode = 'GB'; // Default to GBP for Europe, can be more specific
        } else if (timezone.includes('Asia')) {
          if (timezone.includes('Tokyo')) countryCode = 'JP';
          else if (timezone.includes('Shanghai') || timezone.includes('Beijing')) countryCode = 'CN';
          else if (timezone.includes('Singapore')) countryCode = 'SG';
          else countryCode = 'IN'; // Default to India for Asia
        } else if (timezone.includes('Australia')) {
          countryCode = 'AU';
        }

        setDetectedCountry(countryCode);
        
        const currencyCode = COUNTRY_TO_CURRENCY[countryCode] || 'INR';
        const currency = CURRENCIES.find(c => c.code === currencyCode);
        if (currency) {
          setCurrentCurrency(currency);
        }
      } catch (error) {
        console.log('Could not detect location, using INR as default');
        // Keep INR as default
      }
    };

    detectLocation();
  }, []);

  const changeCurrency = (currencyCode: string) => {
    const currency = CURRENCIES.find(c => c.code === currencyCode);
    if (currency) {
      setCurrentCurrency(currency);
      localStorage.setItem('preferred-currency', currencyCode);
    }
  };

  const convertPrice = (usdPrice: number, toCurrency?: string): number => {
    const targetCurrency = toCurrency 
      ? CURRENCIES.find(c => c.code === toCurrency) 
      : currentCurrency;
    
    if (!targetCurrency) return usdPrice;
    
    return usdPrice * targetCurrency.rate;
  };

  const formatPrice = (usdPrice: number, toCurrency?: string): string => {
    const targetCurrency = toCurrency 
      ? CURRENCIES.find(c => c.code === toCurrency) 
      : currentCurrency;
    
    if (!targetCurrency) return `$${usdPrice.toFixed(2)}`;
    
    const convertedPrice = convertPrice(usdPrice, toCurrency);
    
    // Format according to currency
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: targetCurrency.code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

    return formatter.format(convertedPrice);
  };

  const formatPriceWithSymbol = (usdPrice: number, toCurrency?: string): string => {
    const targetCurrency = toCurrency 
      ? CURRENCIES.find(c => c.code === toCurrency) 
      : currentCurrency;
    
    if (!targetCurrency) return `$${usdPrice.toFixed(2)}`;
    
    const convertedPrice = convertPrice(usdPrice, toCurrency);
    return `${targetCurrency.symbol}${convertedPrice.toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  return {
    currentCurrency,
    currencies: CURRENCIES,
    detectedCountry,
    changeCurrency,
    convertPrice,
    formatPrice,
    formatPriceWithSymbol
  };
};
